import React, { useCallback } from 'react'
import PaginationControls from './PaginationControls'
import PaginationSearch from './PaginationSearch'
import PaginationSize from './PaginationSize'
import { PaginationProps } from './types'

/**
 * Componente principal de Paginación
 * Diseño minimalista, modular y optimizado
 * 
 * @example
 * <Pagination
 *   currentPage={1}
 *   totalPages={10}
 *   pageSize={25}
 *   onPageChange={setCurrentPage}
 *   onPageSizeChange={setPageSize}
 *   searchTerm={searchTerm}
 *   onSearchChange={setSearchTerm}
 *   isLoading={false}
 * />
 */
const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  searchTerm,
  onSearchChange,
  isLoading = false,
  showPageNumbers = true,
  showSearch = true,
  showPageSize = true,
  pageSizeOptions = [30, 50, 60, 80, 100],
  debounceMs = 300,
}) => {
  const handlePageSizeChange = useCallback(
    (size: number) => {
      onPageSizeChange(size)
    },
    [onPageSizeChange]
  )

  const handlePageChange = useCallback(
    (page: number) => {
      onPageChange(page)
    },
    [onPageChange]
  )

  return (
    <div
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-3 shadow-sm z-50"
      role="navigation"
      aria-label="Paginación y controles de tabla"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 items-center">
          {/* Controles de navegación */}
          <div className="flex justify-center sm:justify-start order-1">
            {showPageNumbers && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isLoading={isLoading}
              />
            )}
          </div>

          {/* Búsqueda - posición fija en el centro */}
          <div className="flex justify-center order-2">
            {showSearch && (
              <PaginationSearch
                searchTerm={searchTerm}
                onSearchChange={onSearchChange}
                debounceMs={debounceMs}
                isLoading={isLoading}
              />
            )}
          </div>

          {/* Selector de tamaño - posición fija a la derecha */}
          <div className="flex justify-center sm:justify-end order-3">
            {showPageSize && (
              <PaginationSize
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
                onPageChange={handlePageChange}
                options={pageSizeOptions}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>

        {/* Información adicional opcional */}
        {totalItems !== undefined && (
          <div className="mt-2 text-xs text-gray-500 text-center sm:text-left">
            Mostrando {Math.min((currentPage - 1) * pageSize + 1, totalItems)} -{' '}
            {Math.min(currentPage * pageSize, totalItems)} de {totalItems} resultados
          </div>
        )}
      </div>
    </div>
  )
}

export default React.memo(Pagination)

